import { useTranslation } from 'react-i18next';
import { formatLongDate } from '../list/expense-labels';
import { DecisionSheet } from '../reports/DecisionSheet';
import type { ReportExpenseRow } from '../reports/types';
import type { AllowanceError } from './use-allowance';

/** The longest reason the API takes (decision 203). */
const REASON_MAX = 500;

/**
 * «Togli» on a flat-rate line, for Gestione or an approver (decision 203): asks for the required
 * reason, which the note's «Storia» keeps with who and when, and which reaches the owner.
 */
export function RemoveAllowanceDialog({
  row,
  busy,
  error,
  onConfirm,
  onClose,
}: {
  readonly row: ReportExpenseRow;
  readonly busy: boolean;
  /** The last attempt's refusal: the dialog stays open with the reason typed. */
  readonly error: AllowanceError | null;
  readonly onConfirm: (reason: string) => void;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation('expenses');
  return (
    <DecisionSheet
      title={t('flatRate.remove.title', { day: formatLongDate(row.date) })}
      help={t('flatRate.remove.help')}
      confirmLabel={t('flatRate.remove.confirm')}
      destructive
      busy={busy}
      noteLabel={t('flatRate.remove.reason')}
      noteMissing={t('flatRate.remove.reasonMissing')}
      maxLength={REASON_MAX}
      notice={<AllowanceErrorNotice error={error} />}
      onConfirm={(reason) => onConfirm(reason.trim())}
      onClose={onClose}
    />
  );
}

/** The removal's failure, in words, with what to do about it. */
export function AllowanceErrorNotice({ error }: { readonly error: AllowanceError | null }) {
  const { t } = useTranslation('expenses');
  if (error === null) return null;
  return (
    <p role="alert" className="text-destructive text-sm">
      {t(`flatRate.errors.${error}`)}
    </p>
  );
}
